import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { orderBy, minBy, maxBy } from 'lodash';
import { Observable, Observer } from 'rxjs';
import moment from 'moment';
import { DeviceData } from '../_models';
import { environment } from '../../../environments/environment';
import { GeneralUtilsService } from './common/generalUtils.service';

@Injectable()
export class DeviceDataService {
  data: any = {};
  dataChange: Observable<any>;
  private dataChangeObserver: Observer<any>;

  private apiBaseUrl = environment.apiBaseUrl;
  constructor(
    private http: HttpClient,
    private generalUtilsService: GeneralUtilsService
  ) {
    this.dataChange = new Observable((observer: Observer<any>) => {
      this.dataChangeObserver = observer;
    });
  }
  getDeviceData(
    deviceID: string,
    from: string,
    periodTo: string,
    limit: number,
    onlyImages: boolean
  ): Observable<DeviceData[]> {
    const queryStr =
      '?deviceID=' +
      deviceID +
      '&from=' +
      from +
      '&periodTo=' +
      periodTo +
      '&limit=' +
      limit +
      '&onlyImages=' +
      onlyImages;
    return this.http
      .get<any>(this.apiBaseUrl + 'api/private/v2/sensor/sensorData' + queryStr)
      .pipe(map((response) => response));
  }
  setData(id: any, data: any) {
    this.data[id] = data;
    this.dataChangeObserver.next(this.data);
  }

  getSoilMoisture(lastHumMap) {
    return parseFloat(((15.5 * lastHumMap) / 1000 + 2.58).toFixed(2));
  }

  getLastSensorsConfiguration = (
    data,
    configurationParameterList,
    minMaxCheck: boolean
  ): Array<any> => {
    // tslint:disable-next-line:prefer-const
    const resultListByParam: any = [];
    if (configurationParameterList && configurationParameterList.length !== 0) {
      configurationParameterList.forEach((configurationParameter) => {
        // tslint:disable-next-line:prefer-const
        const lastData = data[data.length - 1];
        if (lastData && lastData[configurationParameter.parameterName] !== undefined) {
          let payload = {};
          if (minMaxCheck) {
            // tslint:disable-next-line:prefer-const
            const minObj = minBy(data, configurationParameter.parameterName);
            // tslint:disable-next-line:prefer-const
            const maxObj = maxBy(data, configurationParameter.parameterName);
            // tslint:disable-next-line:prefer-const
            payload = {
              unit: configurationParameter.parameterUnit,
              parameterName: configurationParameter.parameterName,
              viewOrder: configurationParameter.viewOrder,
              viewName: configurationParameter.viewName,
              value: lastData[configurationParameter.parameterName],
              min: minObj[configurationParameter.parameterName],
              max: maxObj[configurationParameter.parameterName],
              viewAggregation: configurationParameter.viewDataAggregation,
            };
          } else {
            // tslint:disable-next-line:prefer-const
            payload = {
              unit: configurationParameter.parameterUnit,
              parameterName: configurationParameter.parameterName,
              viewOrder: configurationParameter.viewOrder,
              viewName: configurationParameter.viewName,
              value: lastData[configurationParameter.parameterName],
              viewAggregation: configurationParameter.viewDataAggregation,
            };
          }
          resultListByParam.push(payload);
        }
      });
    }
    return orderBy(resultListByParam, ['viewOrder', 'parameterName'], ['asc', 'asc']);
  };

  getLastSensorsConfigurationWithAggregationSteps = (data, configurationParameterList) => {
    // tslint:disable-next-line:prefer-const
    const resultListByParam: any = [];
    if (configurationParameterList && configurationParameterList.length !== 0) {
      configurationParameterList.forEach((configurationParameter) => {
        if (configurationParameter.viewDataAggregation === 'CUMULATED') {
          let payload;
          // tslint:disable-next-line:prefer-const
          payload = {
            unit: configurationParameter.parameterUnit,
            parameterName: configurationParameter.parameterName,
            viewOrder: configurationParameter.viewOrder,
            viewName: configurationParameter.viewName,
            steps: [],
            values: [],
            viewUnit: configurationParameter.viewDataAggregationStepUnit,
            viewAggregation: configurationParameter.viewDataAggregation,
          };

          let index = 0;
          const timestampNow = moment().unix();
          let sumTimestamp = 0;
          let minValideTimestampRange = 0;
          configurationParameter.viewDataAggregationSteps.forEach((step) => {
            let sum = 0;
            if (payload && payload.viewUnit && payload.viewUnit === 'hour') {
              sumTimestamp = step * 3600; // 3600 = 1 hour in timestamp
            } else if (payload && payload.viewUnit && payload.viewUnit === 'day') {
              sumTimestamp = step * 86400; // 86400 = 1 day in timestamp
            }
            minValideTimestampRange = timestampNow - sumTimestamp;
            // prendere viewDataAggregationStepUnit che può essere day o hours e andare indietro di viewDataAggregationStep ottenendo il timestamp
            data.forEach((deviceData) => {
              if (deviceData && deviceData[payload.parameterName] !== undefined) {
                // tslint:disable-next-line:max-line-length
                const convertDate = new Date(deviceData.time);
                const deviceTimeDate: number = Math.floor(convertDate.getTime() / 1000);

                if (deviceTimeDate && deviceTimeDate >= minValideTimestampRange) {
                  sum = sum + deviceData[payload.parameterName];
                }
              }
            });
            payload.values[index] = this.generalUtilsService.roundToDecimals(sum, 2);
            payload.steps[index] =
              step.toString() + ' ' + configurationParameter.viewDataAggregationStepUnit.charAt(0);
            index++;
            resultListByParam.push(payload);
          });
        }
      });
    }
    return orderBy(resultListByParam, ['viewOrder', 'parameterName'], ['asc', 'asc']);
  };

  getLastSensorsConfigurationWithAggregationStep = (data, configurationParameterList) => {
    // tslint:disable-next-line:prefer-const
    const resultListByParam: any = [];
    if (configurationParameterList && configurationParameterList.length !== 0) {
      configurationParameterList.forEach((configurationParameter) => {
        // schianto i dati che trovo così come li trovo nelle ultime viewDataAggregationStep (viewDataAggregationStepUnit)
        if (configurationParameter.viewDataAggregation === 'RAW') {
          let payload;
          // tslint:disable-next-line:prefer-const
          payload = {
            unit: configurationParameter.parameterUnit,
            parameterName: configurationParameter.parameterName,
            viewOrder: configurationParameter.viewOrder,
            viewName: configurationParameter.viewName,
            steps: [],
            values: [],
            viewUnit: configurationParameter.viewDataAggregationStepUnit,
            viewAggregation: configurationParameter.viewDataAggregation,
          };

          let index = 0;
          data.forEach((deviceData) => {
            if (deviceData && deviceData[payload.parameterName] !== undefined) {
              // tslint:disable-next-line:max-line-length
              const currentDate = new Date(deviceData.time as string);
              const time: number = currentDate.getTime();
              payload.values[index] = [time, deviceData[payload.parameterName]];
              index++;
            }
          });
          resultListByParam.push(payload);
        } else if (configurationParameter.viewDataAggregation === 'CUMULATED') {
          let payload;
          // tslint:disable-next-line:prefer-const
          payload = {
            unit: configurationParameter.parameterUnit,
            parameterName: configurationParameter.parameterName,
            viewOrder: configurationParameter.viewOrder,
            viewName: configurationParameter.viewName,
            steps: [],
            values: [],
            viewUnit: configurationParameter.viewDataAggregationStepUnit,
            viewAggregation: configurationParameter.viewDataAggregation,
          };

          let index = 0;
          let sum = 0;
          data.forEach((deviceData) => {
            if (deviceData && deviceData[payload.parameterName] !== undefined) {
              // tslint:disable-next-line:max-line-length
              sum = sum + deviceData[payload.parameterName];
              const currentDate = new Date(deviceData.time as string);
              const time: number = currentDate.getTime();
              payload.values[index] = [time, sum];
              index++;
            }
          });
          resultListByParam.push(payload);
        } else if (configurationParameter.viewDataAggregation === 'SUM') {
          let payload;
          // tslint:disable-next-line:prefer-const
          payload = {
            unit: configurationParameter.parameterUnit,
            parameterName: configurationParameter.parameterName,
            viewOrder: configurationParameter.viewOrder,
            viewName: configurationParameter.viewName,
            step: configurationParameter.viewDataAggregationStep,
            steps: [],
            values: [],
            value: null,
            viewUnit: configurationParameter.viewDataAggregationStepUnit,
            viewAggregation: configurationParameter.viewDataAggregation,
          };

          let index = 0;
          let sum = 0;

          const timestampNow = moment().unix();
          let sumTimestamp = 0;
          let minValideTimestampRange = 0;
          if (
            payload &&
            payload.viewUnit &&
            payload.step &&
            payload.step > 0 &&
            payload.viewUnit === 'hour'
          ) {
            sumTimestamp = payload.step * 3600; // 3600 = 1 hour in timestamp
          } else if (
            payload &&
            payload.viewUnit &&
            payload.step &&
            payload.step > 0 &&
            payload.viewUnit === 'day'
          ) {
            sumTimestamp = payload.step * 86400; // 86400 = 1 day in timestamp
          }
          minValideTimestampRange = timestampNow - sumTimestamp;
          // prendere viewDataAggregationStepUnit che può essere day o hours e andare indietro di viewDataAggregationStep ottenendo il timestamp
          data.forEach((deviceData) => {
            if (deviceData && deviceData[payload.parameterName] !== undefined) {
              // tslint:disable-next-line:max-line-length
              const convertDate = new Date(deviceData.time);
              const deviceTimeDate: number = Math.floor(convertDate.getTime() / 1000);

              if (deviceTimeDate && deviceTimeDate >= minValideTimestampRange) {
                sum = sum + deviceData[payload.parameterName];
              }
              payload.value = sum;
              index++;
            }
          });
          payload.value = this.generalUtilsService.roundToDecimals(payload.value, 2);
          resultListByParam.push(payload);
        }
      });
    }
    return orderBy(resultListByParam, ['viewOrder', 'parameterName'], ['asc', 'asc']);
  };

  getLastDataByViewName = (data, configurationParameterList) => {
    // tslint:disable-next-line:prefer-const
    const resultListByParam: any = [];
    if (configurationParameterList && configurationParameterList.length !== 0) {
      configurationParameterList.forEach((configurationParameter) => {
        // tslint:disable-next-line:prefer-const
        const arrayResult = [];
        if (
          configurationParameter.viewDataAggregationStep &&
          configurationParameter.viewDataAggregationStep !== null
        ) {
          arrayResult.push(
            this.getLastSensorsConfigurationWithAggregationStep(data, [configurationParameter])
          );

          if (arrayResult && arrayResult.length > 0) {
            if (arrayResult[0] && arrayResult[0].length > 0) {
              resultListByParam.push(arrayResult[0][0]);
            } else {
              resultListByParam.push(arrayResult[0]);
            }
          }
        } else if (
          configurationParameter.viewDataAggregationSteps &&
          configurationParameter.viewDataAggregationSteps !== null &&
          configurationParameter.viewDataAggregationSteps.length > 0
        ) {
          // resultListByParam.concat()
          arrayResult.push(
            this.getLastSensorsConfigurationWithAggregationSteps(data, [configurationParameter])
          );

          if (arrayResult && arrayResult.length > 0) {
            if (arrayResult[0] && arrayResult[0].length > 0) {
              resultListByParam.push(arrayResult[0][0]);
            } else {
              resultListByParam.push(arrayResult[0]);
            }
          }
        } else if (
          configurationParameter.viewDataAggregationSteps === null &&
          configurationParameter.viewDataAggregationStep === null
        ) {
          // resultListByParam.concat()
          arrayResult.push(
            this.getLastSensorsConfigurationWithAggregationStep(data, [configurationParameter])
          );
          if (arrayResult && arrayResult.length > 0) {
            if (arrayResult[0] && arrayResult[0].length > 0) {
              resultListByParam.push(arrayResult[0][0]);
            } else {
              resultListByParam.push(arrayResult[0]);
            }
          }
        }
      });
    }
    return orderBy(resultListByParam, ['viewOrder', 'parameterName'], ['asc', 'asc']);
  };

  // questo metodo permette di ottenere l'ultima rilevazione in base a tutti i parametri che si trovano nel mapping
  getLastSensorsConfigurationByAllParameters = (data, configurationParameterList): Array<any> => {
    ////  console.log(configurationParameterList);
    // tslint:disable-next-line:prefer-const
    let resultListByParam: any = [];
    data.forEach((singleData) => {
      let count = 0;
      if (configurationParameterList && configurationParameterList.length !== 0) {
        for (const singleParam of configurationParameterList) {
          if (singleData && singleData[singleParam.parameterName] !== undefined) {
            let payload = {};
            payload = {
              unit: singleParam.parameterUnit,
              parameterName: singleParam.parameterName,
              viewOrder: singleParam.viewOrder,
              value: singleData[singleParam.parameterName],
            };
            resultListByParam[count] = payload;
          } else {
            if (resultListByParam.length < configurationParameterList.length) {
              resultListByParam = [];
            }
            break;
          }
          count++;
        }
      }
    });
    ////  console.log(resultListByParam);

    return orderBy(resultListByParam, ['viewOrder', 'parameterName'], ['asc', 'asc']);
  };

  getDeviceDatav3(
    deviceID: string,
    from: string,
    to: string,
    limit: number,
    onlyImages: boolean
  ): Observable<DeviceData[]> {
    const queryStr =
      '?deviceID=' +
      deviceID +
      '&from=' +
      from +
      '&to=' +
      to +
      '&limit=' +
      limit +
      '&onlyImages=' +
      onlyImages;
    return this.http
      .get<any>(this.apiBaseUrl + 'api/private/v3/sensor/sensorData' + queryStr)
      .pipe(map((response) => response));
  }
}
